/**
 * Componente de mensaje individual del chat
 */

import { useState } from 'react';
import { ChatMessage as ChatMessageType } from '../../types';
import { translateMessage } from '../../services/translationService';
import './ChatMessage.css';

function SpainFlag({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect width="32" height="32" rx="4" fill="#C60B1E" />
      <rect y="8" width="32" height="16" fill="#FFC400" />
      <rect y="24" width="32" height="8" rx="0" fill="#C60B1E" />
      <rect width="32" height="8" fill="#C60B1E" />
    </svg>
  );
}

interface ChatMessageProps {
  message: ChatMessageType;
  onClick?: (message: ChatMessageType) => void;
  onCopy?: (message: ChatMessageType) => void;
}

export function ChatMessage({ message, onClick, onCopy }: ChatMessageProps) {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const [translation, setTranslation] = useState<string | null>(null);
  const [showTranslation, setShowTranslation] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);

  // Mostrar contenido sin los prefijos [audio] y [text] (se guardan en la conversación pero no se muestran en la UI)
  const displayContent = (message.content || '').replace(/^\[(audio|text)\]\s*/i, '');

  // Determinar si la burbuja es clickeable (tiene audio o es para copiar)
  const isClickable = (isAssistant && message.audioUrl) || onClick || onCopy;

  const handleClick = () => {
    if (onClick) {
      onClick(message);
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    // Click derecho para copiar
    if (onCopy) {
      e.preventDefault();
      onCopy(message);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Ctrl+C o Cmd+C para copiar
    if ((e.ctrlKey || e.metaKey) && e.key === 'c' && onCopy) {
      e.preventDefault();
      onCopy(message);
    }
  };

  const handleTranslate = async (e: React.MouseEvent) => {
    e.stopPropagation();

    // Si ya se tradujo, solo toggle visibilidad
    if (translation) {
      setShowTranslation(!showTranslation);
      return;
    }

    setIsTranslating(true);
    try {
      const result = await translateMessage(displayContent);
      setTranslation(result);
      setShowTranslation(true);
    } catch {
      // Silenciar error — el botón simplemente no hace nada
    } finally {
      setIsTranslating(false);
    }
  };

  return (
    <div className={`chat-message ${isUser ? 'user' : 'assistant'}`}>
      <div className="message-bubble-wrapper">
        <div
          className={`message-bubble ${isUser ? 'user-bubble' : 'assistant-bubble'} ${isClickable ? 'clickable' : ''}`}
          onClick={handleClick}
          onContextMenu={handleContextMenu}
          onKeyDown={handleKeyDown}
          tabIndex={isClickable ? 0 : -1}
          role={isClickable ? "button" : undefined}
          aria-label={isAssistant && message.audioUrl ? 'Mensaje con audio. Click para reproducir, Ctrl+C para copiar' : isClickable ? 'Mensaje. Click derecho o Ctrl+C para copiar' : ''}
          title={isAssistant && message.audioUrl ? 'Click para reproducir audio | Ctrl+C para copiar' : isClickable ? 'Click derecho o Ctrl+C para copiar' : ''}
        >
          <p className="message-text">{displayContent}</p>
          {isAssistant && message.audioUrl && (
            <span className="audio-indicator" aria-label="Tiene audio">🔊</span>
          )}
        </div>
        {isAssistant && displayContent.trim().length > 0 && (
          <div className="translate-row">
            <button
              className={`translate-button ${isTranslating ? 'translating' : ''} ${showTranslation ? 'active' : ''}`}
              onClick={handleTranslate}
              disabled={isTranslating}
              aria-label={showTranslation ? 'Ocultar traducción' : 'Ver traducción al español'}
              title={showTranslation ? 'Ocultar traducción' : 'Ver traducción'}
            >
              <SpainFlag size={15} />
            </button>
          </div>
        )}
        {isAssistant && showTranslation && translation && (
          <div className="translation-bubble">
            <p className="message-translation">{translation}</p>
          </div>
        )}
      </div>
    </div>
  );
}


